import { Observable } from 'rxjs';
import axios from 'axios';
import { AxiosPromise } from 'axios';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import BPCApiRouteService from './bpc-api-route.service';
import { RouteServiceInterface } from '@core/interfaces/route-service-interface';
import { ApiRouteConfig } from '@core/interfaces/core-services';

export default class BPCApiServiceInstance {
  protected $bpcRouter: RouteServiceInterface;

  constructor(apiRouteConfig: Partial<ApiRouteConfig> = {}) {
    this.$bpcRouter = new BPCApiRouteService(apiRouteConfig);
  }

  createObservableFromPromise(
    promise: Promise<BPCGenericApiResponse>,
  ): Observable<BPCGenericApiResponse> {
    return new Observable(observer => {
      promise
        .then(response => {
          observer.next(response.data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  createObservable(promise: AxiosPromise): Observable<BPCGenericApiResponse> {
    return new Observable(observer => {
      promise
        .then(response => {
          observer.next(response.data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getBaseUrl(): string {
    return this.$bpcRouter.getBaseUrl();
  }

  get(path: string, config?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(axios.get(apiPath, config));
  }

  delete(path: string, config?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(axios.delete(apiPath, config));
  }

  post(path: string, body?: object, config?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(axios.post(apiPath, body, config));
  }

  postWithMedia(path: string, body?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);
    return this.createObservable(
      axios.post(apiPath, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    );
  }

  put(path: string, body?: object, config?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(axios.put(apiPath, body, config));
  }

  patch(path: string, body?: object, config?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(axios.patch(apiPath, body, config));
  }

  putWithMedia(path: string, body?: object): Observable<BPCGenericApiResponse> {
    const apiPath = this.$bpcRouter.getApiUrl(path);

    return this.createObservable(
      axios.post(apiPath, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    );
  }

  download(path: string, filename: string, config: object): Observable<BPCGenericApiResponse> {
    const promise: Promise<BPCGenericApiResponse> = new Promise((resolve, reject) => {
      const apiPath = this.$bpcRouter.getApiUrl(path);
      axios
        .get(apiPath, { responseType: 'blob', ...config })
        .then(response => {
          const result = this.manageDownloadResponse(response, filename);
          resolve(result);
        })
        .catch(err => {
          reject(err);
        });
    });
    return this.createObservableFromPromise(promise);
  }

  postDownload(
    path: string,
    filename: string,
    body?: object,
    config?: object,
  ): Observable<BPCGenericApiResponse> {
    const promise: Promise<BPCGenericApiResponse> = new Promise((resolve, reject) => {
      const apiPath = this.$bpcRouter.getApiUrl(path);
      axios
        .post(apiPath, body, { responseType: 'blob', ...config })
        .then(response => {
          const result = this.manageDownloadResponse(response, filename);
          resolve(result);
        })
        .catch(err => {
          reject(err);
        });
    });
    return this.createObservableFromPromise(promise);
  }

  private manageDownloadResponse(response, name) {
    const url = window.URL.createObjectURL(new Blob([response.data]));

    let filename = name;
    const disposition = response?.headers?.['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename); //or any other extension
    document.body.appendChild(link);
    link.click();
    return {
      data: {
        filename: filename,
        // more data
      },
    };
  }
}
